import { Ref } from 'vue';
import { IMakeModelData } from './interfaces/IMakeModelData';
import { IReviewDocument } from './interfaces/IReviewSearchResult';
import { uniqBy } from 'lodash-es';
import { IVariantAms } from './interfaces/IVariant';
import { IVehicleGroupAms } from './interfaces/IVehicleGroup';

export type VehicleTypes = 'BEV' | 'HEV' | 'PHEV' | 'ICE';

/**
 * Remove empty values from an Object.
 *
 * @param obj
 * @returns {Object}
 */
export function removeEmpty(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
}

/**
 * Flatten a multidimensional object.
 * For example:
 *
 * flattenObject{ a: 1, b: { c: 2 } }
 * => { a: 1, c: 2 }
 *
 * An "exemptFields" array of key names can be passed in,
 * data inside this key will not be flattened.
 *
 * @param obj
 * @param options
 *
 * @returns {Object}
 */
export function flattenObject(obj: Record<string, any>, options: { exemptFields: string[] }): Record<string, any> {
  const { exemptFields } = options;
  const flattened: Record<string, any> = {};

  if (!obj) return {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (exemptFields?.includes(key)) {
      flattened[key] = value;
      return;
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, options));
    } else {
      flattened[key] = value;
    }
  });

  return flattened;
}

/**
 * Remove empty values from an Array.
 *
 * @param values
 * @returns {any[]}
 */
export function filterEmptyValues(values: any[]): any[] {
  return values.filter(Boolean);
}

/**
 * Test if an HTML element has an active ellipsis (due to CSS "text-overflow: ellipsis")
 *
 * @param Ref
 * @returns {boolean}
 */
export function isEllipsisActive(element: Ref): boolean {
  if (element.value != null) {
    if (element.value.clientWidth < element.value.scrollWidth) {
      var style = element.value.currentStyle || window.getComputedStyle(element.value);
      return style.textOverflow === 'ellipsis';
    }
  }
  return false;
}

/**
 * Check if an Object has any entries
 */
export function objectHasEntries(obj: Record<string, any> | undefined) {
  return obj && Object.keys(obj).length > 0;
}

/**
 * Calculate vehicle type (BEV, HEV, PHEV, ICE)
 * Note: search results have different fields to getVehicleById
 */
export function sitecoreVehicleType(carData: Record<string, any>, isSearchResult: boolean = false): VehicleTypes {
  const carPowertrain = (
    isSearchResult ? carData?.powertrain || carData?.overallPowertrain || '' : carData.Powertrain || carData.Overall_Powertrain || ''
  ).toLowerCase();

  switch (carPowertrain) {
    case 'hybrid electric vehicle (plug-in)':
    case 'plug-in hybrid electric vehicle': {
      return 'PHEV';
    }

    case 'bev':
    case 'electric':
    case 'electric vehicle':
    case 'battery electric vehicle':
    case 'fuel-cell electric vehicle':
    case 'extended-range electric vehicle': {
      return 'BEV';
    }

    case 'hybrid':
    case 'hybrid electric vehicle':
    case 'mild hybrid electric vehicle': {
      return 'HEV';
    }

    default:
      return 'ICE';
  }
}

/**
 * Convert a string to kebab-case
 *
 * @param str
 *
 * @returns 'Hello world' => 'hello-world'
 */
export function toKebabCase(str: string) {
  return str.toLowerCase().replace(/\s+/g, '-');
}
/**
 * This function checks if the store content has been there for longer than configured expiry and needs updating.
 *
 * @param lastUpdatedDate current time when store content was created
 * @param numberOfHoursForStoreContentToExpire number of hours before the content need to expire
 *
 * @returns {Boolean}
 */
export function hasStoreContentExpired(lastUpdatedDate: string, numberOfHoursForStoreContentToExpire: number): boolean {
  const currentSystemTime = new Date(); // Current (Now) date time at the client side

  if (lastUpdatedDate) {
    //if last updated date is set in the store
    var lastUpdatedDateInStore = new Date(lastUpdatedDate);
    lastUpdatedDateInStore.setHours(lastUpdatedDateInStore.getHours() + numberOfHoursForStoreContentToExpire); //appending the configured hours to the current last updated datetime in store
    return lastUpdatedDateInStore <= currentSystemTime;
  }
  return false;
}

export function getRelevantReviewFromList(reviews: IReviewDocument[], trim: string): IReviewDocument {
  if (reviews?.length) {
    if (reviews.length == 1) {
      return reviews[0];
    } else {
      const sortedReviews = reviews.sort((a, b) => new Date(b.document.date_of_car_review).getTime() - new Date(a.document.date_of_car_review).getTime());
      const prioritisedRACQReviews = [
        ...sortedReviews.filter((review) => review.document.mobility_club === 'RACQ'),
        ...sortedReviews.filter((review) => review.document.mobility_club !== 'RACQ'),
      ];
      if (prioritisedRACQReviews?.length == 1 || !trim) return prioritisedRACQReviews[0];
      //if same trim review exists
      var trimBaseReview = prioritisedRACQReviews.filter((x) => x.document.vehicle_trim == trim.toLowerCase());
      if (trimBaseReview?.length) {
        return trimBaseReview[0];
      } else {
        return prioritisedRACQReviews[0];
      }
    }
  }
  return {} as IReviewDocument;
}
/**
 * if the make or model already exists in the data
 * @param makeModelData
 * @param make
 * @param model
 * @returns
 */
function containsMakeOrModel(makeModelData: IMakeModelData[], make: string, model?: string): Boolean {
  const selectedMake = makeModelData?.filter((x) => x.make == make)[0];
  if (selectedMake != null) {
    if (!model) return true;
    else {
      const selectedModel = selectedMake.model.filter((x) => x == model)[0];
      if (selectedModel != null) return true;
    }
  }

  return false;
}
/**
 * Get all make and model combination on the page based on cars passed in
 * @param cars
 * @returns
 */
export function getMakeModelDataFilter(cars: any[]): IMakeModelData[] {
  let makeModelData = [] as IMakeModelData[];
  //if no make model selected in the filter
  if (cars?.length >= 1) {
    cars.forEach((car) => {
      const makeExists = containsMakeOrModel(makeModelData, car.makeNameLowerCase, undefined);
      if (makeExists) {
        const modelExists = containsMakeOrModel(makeModelData, car.makeNameLowerCase, car.modelNameLowerCase);
        if (!modelExists) {
          const selectedMake = makeModelData?.filter((x) => x.make == car.makeNameLowerCase)[0];
          selectedMake.model.push(car.modelNameLowerCase);
        }
      } else {
        makeModelData.push({ make: car.makeNameLowerCase, model: [car.modelNameLowerCase] } as IMakeModelData);
      }
    });
  }

  return makeModelData;
}
/**
 * Replaces special charactors from the give string
 * @param value input string
 * @returns
 */
export function removeSpecialCharacters(value: string | undefined): string | undefined {
  if (value) {
    value = value.replaceAll(' ', '-');
    value = value.replaceAll('[^a-zA-Z0-9_-]', '');
  }
  return value;
}

/**
 * Normalize a string with accents
 * Example: "Crème Brulée" => "Creme Brulee"
 *
 * @param str
 * @returns {String}
 */
function normalizeAccentedText(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function removeDuplicateModelsFromCollection(list: any[]) {
  return uniqBy(list, (data) => normalizeAccentedText(data.modelName));
}

export function removeDuplicateBadgesFromCollection(list: any[]) {
  return uniqBy(list, (data) => normalizeAccentedText(data.badgeName));
}

/**
 * Calculates the racqPrice and ensures that values are correctly ordered when undefined
 */
export function calculateRacqPrice(variantItem: IVariantAms, mappedResponse: IVehicleGroupAms, sortDirection: 'asc' | 'desc', fieldToSort: string) {
  const vehicleIsCurrent = mappedResponse.vehicle_status_correct_case?.toLowerCase() === 'current';

  // If we're not sorting on racqPrice, we don't need to handle anything differently
  if (fieldToSort !== 'racqPrice') {
    if (vehicleIsCurrent) {
      return variantItem.qld_driveawayprice;
    } else {
      return variantItem.price_current_retail_value_average || 0;
    }
  }

  if (vehicleIsCurrent) {
    // All current vehicles should have a qld_driveawayprice
    return variantItem.qld_driveawayprice;
  }

  // Vehicle is used

  if (variantItem.price_current_retail_value_average !== undefined) {
    // Return the used price if it has a value
    return variantItem.price_current_retail_value_average;
  }

  if (sortDirection === 'asc') {
    // Return undefined so lodash puts at the end
    return undefined;
  }
  if (sortDirection === 'desc') {
    // Return empty string so lodash puts at end
    return '';
  }
}
